import { motion } from "framer-motion";
import { Play, ArrowUpRight } from "lucide-react";
import { VIDEO_LINKS, REEL_ID } from "@/content";
import { useLanguage } from "@/lang";
import { SectionHeading } from "./SectionHeading";

export const Showreel = () => {
  const { copy } = useLanguage();

  return (
  <section id="showreel" data-testid="showreel-section" className="border-y border-white/10 bg-[#0a0a0a]">
    <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-32">
      <SectionHeading {...copy.headings.showreel} />

      <motion.div
        data-testid="showreel-player"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="border border-white/10 mb-12"
      >
        <div className="aspect-video">
          <iframe
            data-testid="showreel-iframe"
            src={`https://www.youtube-nocookie.com/embed/${REEL_ID}?rel=0`}
            title="Clive Bucher — Showreel"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full grayscale hover:grayscale-0 transition-[filter] duration-700"
          />
        </div>
        <div className="flex items-center justify-between px-5 md:px-6 py-4 border-t border-white/10">
          <h3 className="font-display text-xl md:text-2xl tracking-tight">Showreel</h3>
          <a
            data-testid="showreel-youtube-link"
            href={`https://youtu.be/${REEL_ID}`}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-white transition-colors duration-300"
          >
            {copy.videoSection.youtubeLink}
            <ArrowUpRight size={15} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
          </a>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {VIDEO_LINKS.map((video, i) => {
          const card = copy.videoSection.cards[video.key];
          return (
          <motion.a
            key={video.key}
            data-testid={`video-card-${i}`}
            href={video.href}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
            className="group relative block border border-white/10 overflow-hidden"
          >
            <div className="aspect-[16/9] overflow-hidden">
              <img
                src={video.img}
                alt={card.title}
                loading="lazy"
                className="w-full h-full object-cover grayscale group-hover:scale-[1.05] transition-transform duration-700 ease-out"
              />
            </div>
            <div className="absolute inset-0 bg-black/40 group-hover:bg-black/25 transition-[background-color] duration-500" />

            <div className="absolute inset-0 flex items-center justify-center">
              <span className="w-14 h-14 rounded-full border border-white/40 bg-black/40 backdrop-blur-md flex items-center justify-center group-hover:scale-110 group-hover:border-white transition-[transform,border-color] duration-500">
                <Play size={18} className="text-white fill-white ml-0.5" />
              </span>
            </div>

            <div className="absolute bottom-0 left-0 right-0 flex items-end justify-between p-5">
              <div>
                <h3 className="font-display text-xl tracking-tight">{card.title}</h3>
                <p className="text-xs uppercase tracking-[0.2em] text-zinc-400 mt-1">{card.meta}</p>
              </div>
              <ArrowUpRight size={18} className="text-zinc-400 group-hover:text-white group-hover:translate-x-1 group-hover:-translate-y-1 transition-[color,transform] duration-300" />
            </div>
          </motion.a>
          );
        })}
      </div>
    </div>
  </section>
  );
};
